import { useFileDialog } from '@vueuse/core';
import { v4 as uuid } from 'uuid';
import { computed, ref, watch } from 'vue';

import { useAlertsStore } from '@tallo/design-system';

import { UploadFile } from '../interfaces/upload-file.interface';

export interface FileUploaderOptions {
  multiple?: boolean;
  accept?: string;
  capture?: 'user' | 'environment';
  uploadFile: (file: File, onProgress: (loaded: number) => void) => Promise<any> | any;
  beforeUpload?: () => Promise<void> | void;
}

const defaultOptions: Omit<FileUploaderOptions, 'uploadFile'> = {
  multiple: false,
  accept: '*',
};

export function useFileUploader(fileUploaderOptions: FileUploaderOptions) {
  const options = Object.assign({ ...defaultOptions }, { ...fileUploaderOptions });
  const alertsStore = useAlertsStore();
  const fileDialog = useFileDialog({
    ...options,
    reset: true,
  });

  const uploadQueue = ref<UploadFile[]>([]);
  const uploadingQueue = ref<UploadFile[]>([]);
  const batchUploadLimit = 6;
  const uploadingItemsCount = computed(() => uploadingQueue.value.length);
  const availableSlotsForUpload = computed(() => batchUploadLimit - uploadingItemsCount.value);
  const uploadFiles = computed<UploadFile[]>(() => uploadQueue.value.concat(uploadingQueue.value));
  const isUploading = computed(() => uploadFiles.value.length > 0);

  fileDialog.onChange((files) => {
    if (files) {
      addFilesToQueue(files);
    }
  });

  watch(availableSlotsForUpload, () => uploadBatchFilesFromQueue());

  async function addFilesToQueue(fileList: FileList | File[]) {
    const files = Array.from(fileList)
      .map((file) => ({
        uploaded: 0,
        file,
        id: uuid(),
      }))
      .filter((uploadFile) => {
        const allowedTypes = (options.accept || '*')
          .split(',')
          // eslint-disable-next-line no-useless-escape
          .map((allowedType) => new RegExp(allowedType.replace(' ', '').replace('*', '.*')));

        return allowedTypes.some((allowedType) => allowedType.test(uploadFile.file.type));
      });

    if (!files.length) {
      return;
    }

    uploadQueue.value = uploadQueue.value.concat(files);

    if (options.beforeUpload) {
      await options.beforeUpload();
    }

    uploadBatchFilesFromQueue();
  }

  function uploadBatchFilesFromQueue() {
    const awaitingUploadCount = uploadQueue.value.length;
    const availableSlots = availableSlotsForUpload.value;

    if (!availableSlotsForUpload.value || !awaitingUploadCount) {
      return;
    }

    const nextBatch = awaitingUploadCount >= availableSlots ? availableSlots : awaitingUploadCount;
    const filesToUpload = [...uploadQueue.value].splice(0, nextBatch);

    uploadQueue.value = uploadQueue.value.slice(nextBatch);
    uploadingQueue.value = [...uploadingQueue.value, ...filesToUpload];

    filesToUpload.forEach((uploadFile) => upload(uploadFile));
  }

  async function upload(uploadFile: UploadFile) {
    try {
      await fileUploaderOptions.uploadFile(uploadFile.file, (uploaded) => {
        const uploadingFileIndex = uploadingQueue.value.findIndex(({ id }) => id === uploadFile.id);

        if (uploadingFileIndex >= 0) {
          uploadingQueue.value[uploadingFileIndex].uploaded = uploaded;
        }
      });
    } catch {
      alertsStore.error(`Unable to upload ${uploadFile.file.name}`);
    }

    const uplaodedFileIndex = uploadingQueue.value.findIndex((uploadingFile) => uploadingFile.id === uploadFile.id);

    if (uplaodedFileIndex !== -1) {
      uploadingQueue.value.splice(uplaodedFileIndex, 1);
    }
  }

  return {
    options,
    uploadFiles,
    isUploading,
    addFilesToQueue,
    openFileDialog: fileDialog.open,
  };
}
